import React from 'react';
import classNames from 'classnames';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import CloseButton from '../CloseButton';
import { motion } from 'framer-motion';

/**
 * Drawer component is a custom component to render a drawer.
 * It uses the `classNames` utility to conditionally apply classes.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The children of the drawer.
 * @param {string} [props.className] - The class name of the drawer.
 * @param {boolean} [props.closable] - The flag to check if the drawer is closable.
 * @param {string | number} [props.width] - The width of the drawer.
 * @param {string | number} [props.height] - The height of the drawer.
 * @param {boolean} props.isOpen - The flag to check if the drawer is open.
 * @param {function} props.onClose - The function to call when the drawer closes.
 * @param {number} [props.closeTimeoutMS] - The close timeout in milliseconds.
 * @param {string} [props.placement] - The placement of the drawer.
 * @param {string} [props.bodyOpenClassName] - The class name of the body when the drawer is open.
 * @param {string} [props.portalClassName] - The class name of the portal.
 * @param {string} [props.overlayClassName] - The class name of the overlay.
 * @param {React.ReactNode} [props.title] - The title of the drawer.
 * @param {React.ReactNode} [props.footer] - The footer of the drawer.
 * @param {string} [props.headerClass] - The class name of the header.
 * @param {string} [props.footerClass] - The class name of the footer.
 * @param {string} [props.bodyClass] - The class name of the body.
 * @param {boolean} [props.showBackdrop] - The flag to show the backdrop.
 * @param {boolean} [props.lockScroll] - The flag to lock the scroll.
 * @returns {React.ReactElement}
 */
const Drawer = (props) => {
    const {
        children,
        className,
        closable,
        width,
        height,
        isOpen,
        onClose,
        closeTimeoutMS,
        placement,
        bodyOpenClassName,
        portalClassName,
        overlayClassName,
        title,
        footer,
        headerClass,
        footerClass,
        bodyClass,
        showBackdrop,
        lockScroll,
        ...rest
    } = props;

    const onCloseClick = (e) => {
        onClose(e);
    };

    const renderCloseButton = <CloseButton onClick={onCloseClick} />;

    const getStyle = () => {
        if (placement === 'left' || placement === 'right') {
            return {
                dimensionClass: 'vertical',
                contentStyle: { width },
                motionStyle: {
                    [placement]: `-${width}${
                        typeof width === 'number' && 'px'
                    }`,
                },
            };
        }

        if (placement === 'top' || placement === 'bottom') {
            return {
                dimensionClass: 'horizontal',
                contentStyle: { height },
                motionStyle: {
                    [placement]: `-${height}${
                        typeof height === 'number' && 'px'
                    }`,
                },
            };
        }
    };

    const { dimensionClass, contentStyle, motionStyle } = getStyle();

    return (
        <Modal
            className={{
                base: classNames('drawer', className),
                afterOpen: 'drawer-after-open',
                beforeClose: 'drawer-before-close',
            }}
            overlayClassName={{
                base: classNames(
                    'drawer-overlay',
                    overlayClassName,
                    !showBackdrop && 'bg-transparent'
                ),
                afterOpen: 'drawer-overlay-after-open',
                beforeClose: 'drawer-overlay-before-close',
            }}
            portalClassName={classNames('drawer-portal', portalClassName)}
            bodyOpenClassName={classNames(
                'drawer-open',
                lockScroll && 'drawer-lock-scroll',
                bodyOpenClassName
            )}
            ariaHideApp={false}
            isOpen={isOpen}
            closeTimeoutMS={closeTimeoutMS}
            {...rest}
        >
            <motion.div
                className={classNames('drawer-content', dimensionClass)}
                style={contentStyle}
                initial={motionStyle}
                animate={{
                    [placement]: isOpen ? 0 : motionStyle[placement],
                }}
            >
                {title || closable ? (
                    <div className={classNames('drawer-header', headerClass)}>
                        {typeof title === 'string' ? (
                            <h4>{title}</h4>
                        ) : (
                            <span>{title}</span>
                        )}
                        {closable && renderCloseButton}
                    </div>
                ) : null}
                <div className={classNames('drawer-body', bodyClass)}>
                    {children}
                </div>
                {footer && (
                    <div className={classNames('drawer-footer', footerClass)}>
                        {footer}
                    </div>
                )}
            </motion.div>
        </Modal>
    );
};

Drawer.propTypes = {
    placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    width: PropTypes.number,
    height: PropTypes.number,
    closable: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    footer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    headerClass: PropTypes.string,
    footerClass: PropTypes.string,
    bodyClass: PropTypes.string,
    showBackdrop: PropTypes.bool,
    lockScroll: PropTypes.bool,
    bodyOpenClassName: PropTypes.string,
    portalClassName: PropTypes.string,
    overlayClassName: PropTypes.string,
};

Drawer.defaultProps = {
    closable: true,
    width: 400,
    height: 400,
    closeTimeoutMS: 300,
    placement: 'right',
    showBackdrop: true,
    lockScroll: true,
};

export default Drawer;
